import React from "react";

import Image from "next/image";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { PageManifestoSchema } from "@/sanity/pages/PageManifestoTemplate";

import movement from "@/public/manifesto/movement.svg";
import arrowDown from "@/public/manifesto/arrow-down-icon.svg";
import arrowUpRight from "@/public/manifesto/arrow-up-right.svg";

export default function Manifesto({ data }: { data: PageManifestoSchema }) {
  return (
    <section className="w-full overflow-clip">
      {/* lines */}
      <div className="h-[.375rem] min-h-[.375rem] flex flex-col w-[179rem] min-w-[179rem] mx-auto overflow-clip lg:mb-[6rem] mb-[4rem]">
        <span className="w-full min-w-full h-[.125rem] min-h-[.125rem] bg-[#202223]/20"></span>
        <span className="w-full min-w-full h-[.25rem] min-h-[.25rem] bg-white"></span>
      </div>
      {/* end lines */}
      <div className="w-full max-w-[111rem] mx-auto px-[2rem] lg:px-0">
        <div className="w-full">
          <div className="w-fit flex items-center bg-white mb-[1rem] lg:mb-[2rem] mx-auto">
            <div className="w-[4rem] bg-[#D9F9B9] p-2">
              <Image src={movement} alt="movement" className="!w-full" />
            </div>
            <span className="font-geist text-black text-[2rem] font-[400] leading-[100%] px-[1rem] py-[1rem] uppercase">
              {data?.heroPill?.text}
            </span>
          </div>

          {data?.manifestoTitle && (
            <PortableText
              value={data?.manifestoTitle ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="font-funnel text-black text-[4.5rem] lg:text-[6rem] leading-[100%] font-[500] mb-[2rem] text-center">
                      {children}
                    </h2>
                  ),
                },
              })}
            />
          )}
        </div>
      </div>

      {/* lines */}
      <div className="h-[.375rem] min-h-[.375rem] flex flex-col w-[179rem] min-w-[179rem] mx-auto overflow-clip lg:mt-[6rem] mt-[4rem]">
        <span className="w-full min-w-full h-[.125rem] min-h-[.125rem] bg-[#202223]/20"></span>
        <span className="w-full min-w-full h-[.25rem] min-h-[.25rem] bg-white"></span>
      </div>
      {/* end lines */}

      <div className="w-full max-w-[111rem] mx-auto px-[2rem] lg:px-0 grid lg:grid-cols-2 lg:justify-between relative">
        {/* vertical lines */}
        <div className="hidden lg:flex absolute top-0 left-1/2 -translate-x-1/2 w-[.375rem] min-w-[.375rem] h-full min-h-full">
          <span className="w-[0.125rem] min-w-[0.125rem] h-full min-h-full bg-[#202223]/20"></span>
          <span className="w-[0.25rem] min-w-[0.25rem] h-full min-h-full bg-white"></span>
        </div>
        {/* end vertical lines */}
        <div className="w-full mt-[2rem] lg:mt-[4.25rem] lg:mb-[8rem] mb-[4rem]">
          <GroqImage
            src={data?.manifestoImage}
            alt="Manifesto image"
            className="w-full max-w-[50rem] max-h-[40rem] lg:max-h-max object-cover object-bottom"
          />
        </div>
        <div className="w-full lg:ps-[3rem] lg:mt-[4.25rem] lg:mb-[8rem] mb-[4rem]">
          {data?.manifestoTexts1 && (
            <PortableText
              value={data?.manifestoTexts1 ?? []}
              components={customRichTextComponent({
                block: {
                  normal: ({ children }) => (
                    <p className="text-black/60 font-[400] leading-[150%] mb-0 text-[2.25rem]">
                      {children}
                    </p>
                  ),
                },
                marks: {
                  strong: ({ children }) => (
                    <span className="text-black">{children}</span>
                  ),
                },
              })}
            />
          )}

          <div className="w-full h-[6.5rem] flex items-center bg-white my-[2rem] mx-auto">
            <div className="h-full min-h-full bg-[#D9F9B9] px-[1.5rem] flex justify-center items-center">
              <Image src={arrowDown} alt="arrow down" className="!w-[2.5rem]" />
            </div>
            <div className="w-full flex justify-between">
              <div className="flex flex-col px-[1rem]">
                <span className="text-black text-[2rem] font-[400] leading-[100%]">
                  {data?.settings?.language === "en"
                    ? "But the crucial point is"
                    : "Doch das Entscheidende ist"}
                  :
                </span>
                <span className="text-black/70 text-[2rem] font-[400] leading-[100%]">
                  {data?.settings?.language === "en"
                    ? "We make it accessible"
                    : "Wir machen es zugänglich"}
                  .
                </span>
              </div>
              <Image
                src={arrowUpRight}
                alt="arrow up right"
                className="!w-[3rem] me-[2rem]"
              />
            </div>
          </div>

          {data?.manifestoTexts2 && (
            <PortableText
              value={data?.manifestoTexts2 ?? []}
              components={customRichTextComponent({
                block: {
                  normal: ({ children }) => (
                    <p className="text-black/60 font-[400] leading-[150%] mb-0 text-[2.25rem]">
                      {children}
                    </p>
                  ),
                },
                marks: {
                  strong: ({ children }) => (
                    <span className="text-black">{children}</span>
                  ),
                },
              })}
            />
          )}
        </div>
      </div>

      {/* lines */}
      <div className="h-[.375rem] min-h-[.375rem] flex flex-col w-[179rem] min-w-[179rem] mx-auto overflow-clip">
        <span className="w-full min-w-full h-[.125rem] min-h-[.125rem] bg-[#202223]/20"></span>
        <span className="w-full min-w-full h-[.25rem] min-h-[.25rem] bg-white"></span>
      </div>
      {/* end lines */}
    </section>
  );
}
